import { useEffect, useState } from "react";
import { listSignedQuotes, quotePdfUrl, type FxSignedQuote } from "./facturx";
import { listSignDocs, listSignFolders, readSignDoc, sendSignDoc, signFileUrl, type SignDoc, type SignFolder, type SignReady } from "./sign";
import { useAccess } from "./access";
import { Modal } from "./Modal";
import { aimedFind, clearFind } from "./find";

const STATUS: Record<string, string> = {
  draft: "Brouillon",
  sent: "En attente",
  signed: "Signé",
  viewed: "Consulté",
  declined: "Refusé",
};

const TONE: Record<string, string> = {
  draft: "bg-slate-100 text-slate-600",
  sent: "bg-amber-100 text-amber-800",
  signed: "bg-emerald-100 text-emerald-800",
  viewed: "bg-sky-100 text-sky-800",
  declined: "bg-rose-100 text-rose-800",
};

type OpenDoc = { kind: "sign"; doc: SignDoc } | { kind: "quote"; doc: FxSignedQuote };

export function SignBlock({ folderId, companyId, clientId, email, phone, ficheName, ficheId }: { folderId: number; companyId: number; clientId: number; email: string; phone: string; ficheName: string; ficheId: number }) {
  const { canWrite } = useAccess();
  const [rows, setRows] = useState<SignDoc[]>([]);
  const [quotes, setQuotes] = useState<FxSignedQuote[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<OpenDoc | null>(null);
  const [sending, setSending] = useState<SignDoc | null>(null);
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setRows([]);
    setQuotes([]);
    setError("");
    void listSignDocs(folderId)
      .then((list) => {
        if (alive) setRows(list);
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Contrats indisponibles");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    if (companyId > 0 && clientId > 0) {
      void listSignedQuotes(companyId, clientId)
        .then((devis) => {
          if (alive) setQuotes(devis);
        })
        .catch(() => {
          /* les devis signés restent absents si FacturX ne répond pas */
        });
    }
    return () => {
      alive = false;
    };
  }, [folderId, companyId, clientId, pulse]);

  useEffect(() => {
    const openAimed = () => {
      const hit = aimedFind("Signature", ficheId);
      if (!hit || rows.length === 0) return;
      const doc = rows.find((row) => row.id === hit.signId);
      if (!doc) return;
      setOpen({ kind: "sign", doc });
      clearFind();
    };
    openAimed();
    window.addEventListener("crm-find", openAimed);
    return () => window.removeEventListener("crm-find", openAimed);
  }, [rows, ficheId]);

  return (
    <div className="crm-scroll max-h-72 overflow-auto">
      {loading ? <p className="text-sm text-slate-500">Chargement des contrats…</p> : null}
      {!loading && !error && rows.length === 0 && quotes.length === 0 ? <p className="text-sm text-slate-500">Aucun contrat dans ce dossier.</p> : null}
      {quotes.map((row) => (
        <div key={`q-${row.id}`} className="flex w-full cursor-pointer items-start gap-2.5 border-b border-slate-200 px-1 py-2.5 text-left last:border-b-0 hover:bg-black/5" onDoubleClick={() => setOpen({ kind: "quote", doc: row })}>
          <span className="crm-fill grid h-8 w-8 shrink-0 place-items-center rounded-full text-white">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
              <path d="M14 3v5h5M8 13h8M8 17h5" />
            </svg>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-semibold">{row.number}</span>
            <span className="mt-0.5 block truncate text-[11px] text-slate-500">{row.signer ? `Devis signé · ${row.signer}` : "Devis signé"}</span>
          </span>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${TONE.signed}`}>Signé</span>
        </div>
      ))}
      {rows.map((row) => (
        <div key={row.id} className="flex w-full cursor-pointer items-start gap-2.5 border-b border-slate-200 px-1 py-2.5 text-left last:border-b-0 hover:bg-black/5" onDoubleClick={() => setOpen({ kind: "sign", doc: row })}>
          <span className="crm-fill grid h-8 w-8 shrink-0 place-items-center rounded-full text-white">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
              <path d="M12 19l-7 2 2-7L15.5 5.5a2.1 2.1 0 0 1 3 3Z" />
            </svg>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-semibold">{row.title}</span>
            <span className="mt-0.5 block truncate text-[11px] text-slate-500">{folderId === 0 && row.folder ? row.folder : ""}</span>
          </span>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${TONE[row.status] || TONE.draft}`}>{STATUS[row.status] || row.status}</span>
          {canWrite && row.status === "draft" ? (
            <button type="button" className="crm-fill shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold text-white" onClick={(event) => { event.stopPropagation(); setSending(row); }}>Envoyer</button>
          ) : null}
        </div>
      ))}
      {error ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
      {sending ? (
        <SendBridge docId={sending.id} title={sending.title} ficheName={ficheName} email={email} phone={phone} onClose={() => setSending(null)} onSent={() => { setSending(null); setPulse((value) => value + 1); }} />
      ) : null}
      {open ? (
        <Modal onClose={() => setOpen(null)} sheetClass="crm-pop flex h-[78vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {(close) => (
            <>
              <div className="flex items-center gap-2">
                <h2 className="min-w-0 flex-1 truncate text-lg font-semibold">{open.kind === "quote" ? open.doc.number : open.doc.title}</h2>
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${open.kind === "quote" ? TONE.signed : TONE[open.doc.status] || TONE.draft}`}>{open.kind === "quote" ? "Devis signé" : STATUS[open.doc.status] || open.doc.status}</span>
              </div>
              <iframe title={open.kind === "quote" ? open.doc.number : open.doc.title} className="mt-3 min-h-0 flex-1 rounded-xl border border-slate-200 bg-white" src={open.kind === "quote" ? quotePdfUrl(open.doc.id) : signFileUrl(open.doc)} />
            </>
          )}
        </Modal>
      ) : null}
    </div>
  );
}

function SendBridge({ docId, title, ficheName, email, phone, onClose, onSent }: { docId: number; title: string; ficheName: string; email: string; phone: string; onClose: () => void; onSent: () => void }) {
  const [doc, setDoc] = useState<SignReady | null>(null);
  const [sender, setSender] = useState(ficheName);
  const [mail, setMail] = useState(email);
  const [tel, setTel] = useState(phone);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    void readSignDoc(docId)
      .then((ready) => {
        if (!alive) return;
        setDoc(ready);
        const party = ready.parties[0];
        setSender(ready.sender_name.trim() || ficheName);
        setMail((party?.email || "").trim() || email);
        setTel((party?.phone || "").trim() || phone);
      })
      .catch((reason: unknown) => { if (alive) setError(reason instanceof Error ? reason.message : "Contrat illisible"); });
    return () => { alive = false; };
  }, [docId, email, phone, ficheName]);

  async function go() {
    if (!doc || busy) return;
    setBusy(true);
    setError("");
    try {
      const mailed = await sendSignDoc(doc, sender.trim(), mail.trim(), tel.trim());
      setNote(mailed.length > 0 ? `Invitation envoyée à ${mailed.join(", ")}.` : "Invitation envoyée.");
      setBusy(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Envoi impossible");
      setBusy(false);
    }
  }

  return (
    <Modal onClose={() => { if (note) onSent(); else onClose(); }} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-5">
      {() => (
        <div className="grid gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] crm-ink">Signature</p>
          <h2 className="text-lg font-semibold">Envoyer « {title} »</h2>
          <p className="text-sm text-slate-500">Le CRM demande à Signature d’envoyer l’invitation. Le contrat et le code d’accès restent ceux de Signature.</p>
          <label className="grid gap-1 text-sm">
            <span className="font-medium">Nom vu par le destinataire</span>
            <input className="crm-field h-10 rounded-xl border px-3" value={sender} onChange={(event) => setSender(event.target.value)} />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="font-medium">E-mail</span>
            <input className="crm-field h-10 rounded-xl border px-3" type="email" value={mail} onChange={(event) => setMail(event.target.value)} />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="font-medium">Téléphone</span>
            <input className="crm-field h-10 rounded-xl border px-3" value={tel} onChange={(event) => setTel(event.target.value)} />
          </label>
          {note ? <p className="text-sm text-emerald-700">{note}</p> : null}
          {error ? <p className="text-sm text-rose-700">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <button type="button" className="crm-pill rounded-full px-4 py-2 text-sm font-semibold" onClick={() => { if (note) onSent(); else onClose(); }}>Fermer</button>
            <button type="button" className="crm-fill rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" disabled={busy || !doc || !!note} onClick={() => void go()}>{busy ? "Envoi…" : "Envoyer"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function SignPicker({ onClose, onChoose }: { onClose: () => void; onChoose: (folderId: number) => Promise<void> }) {
  const [folders, setFolders] = useState<SignFolder[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void listSignFolders()
      .then(setFolders)
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Dossiers indisponibles"));
  }, []);

  function pick(folderId: number, close: () => void) {
    if (busy) return;
    setBusy(true);
    setError("");
    void onChoose(folderId)
      .then(() => close())
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Dossier non retenu");
        setBusy(false);
      });
  }

  return (
    <Modal onClose={onClose} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] crm-ink">Signature</p>
          <h2 className="mt-1 text-lg font-semibold">Choisir le dossier</h2>
          <p className="mt-1 text-sm text-slate-500">La racine montre tous les contrats. Un dossier n’en montre que le sien.</p>
          <ul className="crm-scroll mt-4 max-h-72 overflow-auto">
            <li>
              <button type="button" className="flex w-full items-center justify-between px-2 py-2.5 text-left text-sm hover:bg-black/5" disabled={busy} onClick={() => pick(0, close)}>
                <span className="font-semibold">Racine</span>
                <span className="text-xs text-slate-500">Tout</span>
              </button>
            </li>
            {folders.map((folder) => (
              <li key={folder.id} className="border-t border-slate-200">
                <button type="button" className="flex w-full items-center justify-between px-2 py-2.5 text-left text-sm hover:bg-black/5" disabled={busy} onClick={() => pick(folder.id, close)}>
                  <span className="truncate font-medium">{folder.name}</span>
                  <span className="shrink-0 text-xs text-slate-500">{folder.count}</span>
                </button>
              </li>
            ))}
          </ul>
          {error ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}
        </>
      )}
    </Modal>
  );
}
